'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiTag } from '@shared/all/domains/factiiis';
import { convertToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import AddFactiiiModal from '../spaces/AddFactiiiModal';
import Input from '../UI/Input';
import { FactiiiSearchResult } from './FactiiiSearchResult';
type Props = {
  myPostFactiiiCounts: FactiiiTag[];
  handleClick: (tag: FactiiiTag) => void;
  selectedTag?: FactiiiTag;
  /**
   * Limit results to one space. Set from the route by the caller, so opening
   * this on a board searches that board first — the factiiis you want are
   * almost always the ones already in use here. Undefined searches everywhere.
   */
  spaceSlug?: string;
};
export const FactiiiSearch = props => {
  const $ = _c(44);
  const [factiiiQuery, setFactiiiQuery] = useState("");
  const [addFactiiiModalOpen, setAddFactiiiModalOpen] = useState(false);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] !== factiiiQuery || $[1] !== props.spaceSlug) {
    t0 = ["myFactiiiQuery", factiiiQuery, props.spaceSlug];
    $[0] = factiiiQuery;
    $[1] = props.spaceSlug;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== factiiiQuery || $[4] !== props.myPostFactiiiCounts || $[5] !== props.selectedTag || $[6] !== props.spaceSlug) {
    t1 = () => unwrapFromResult(client.search.factiiis.query({
      query: factiiiQuery,
      spaceSlug: props.spaceSlug,
      ignoredIds: [...(props.selectedTag?.factiiiId ? [props.selectedTag.factiiiId] : []), ...props.myPostFactiiiCounts.map(_temp)]
    }));
    $[3] = factiiiQuery;
    $[4] = props.myPostFactiiiCounts;
    $[5] = props.selectedTag;
    $[6] = props.spaceSlug;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      refetchOnWindowFocus: false,
      placeholderData: _temp2,
      staleTime: 30000
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const {
    data: searchData,
    refetch
  } = useQuery(t2);
  let t3;
  if ($[11] !== props.myPostFactiiiCounts) {
    t3 = new Set(props.myPostFactiiiCounts.map(_temp3));
    $[11] = props.myPostFactiiiCounts;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const selectedIds = t3;
  let searchTags;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[13] !== factiiiQuery || $[14] !== props || $[15] !== searchData?.factiiis || $[16] !== selectedIds) {
    let t8;
    if ($[22] !== selectedIds) {
      t8 = tag_0 => !selectedIds.has(tag_0.id);
      $[22] = selectedIds;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    searchTags = (searchData?.factiiis || []).filter(t8).map(_temp4);
    t6 = "flex flex-col";
    if ($[24] !== factiiiQuery) {
      t7 = <Input placeholder="Search for factiiis..." value={factiiiQuery} onChange={setFactiiiQuery} />;
      $[24] = factiiiQuery;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    t4 = "mt-2 space-y-2";
    t5 = searchTags.length > 0 && <>{searchTags.map(tag_2 => <FactiiiSearchResult key={tag_2.factiiiId} tag={tag_2} replyConfig={tag_2.replyConfig} handleClick={() => props.handleClick(tag_2)} />)}</>;
    $[13] = factiiiQuery;
    $[14] = props;
    $[15] = searchData?.factiiis;
    $[16] = selectedIds;
    $[17] = searchTags;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    searchTags = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
  }
  let t8;
  if ($[26] !== factiiiQuery.length || $[27] !== props.myPostFactiiiCounts.length || $[28] !== searchTags) {
    t8 = factiiiQuery.length > 0 && searchTags.length === 0 && props.myPostFactiiiCounts.length === 0 && <div className="py-4"><p className="mb-2 text-center text-sm text-textSecondary">No factiiis found, click below to create a new one...</p><Button onClick={() => setAddFactiiiModalOpen(true)} text="Create New Factiii" icon={<Plus />} classNames="w-full" /></div>;
    $[26] = factiiiQuery.length;
    $[27] = props.myPostFactiiiCounts.length;
    $[28] = searchTags;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t4 || $[31] !== t5 || $[32] !== t8) {
    t9 = <div className={t4}>{t5}{t8}</div>;
    $[30] = t4;
    $[31] = t5;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== addFactiiiModalOpen || $[35] !== currentUser || $[36] !== factiiiQuery || $[37] !== refetch) {
    t10 = addFactiiiModalOpen && <AddFactiiiModal closeHandler={() => setAddFactiiiModalOpen(false)} type="USER" slug={currentUser.username} refetch={refetch} searchedName={factiiiQuery} />;
    $[34] = addFactiiiModalOpen;
    $[35] = currentUser;
    $[36] = factiiiQuery;
    $[37] = refetch;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t6 || $[41] !== t7 || $[42] !== t9) {
    t11 = <div className={t6}>{t7}{t9}{t10}</div>;
    $[39] = t10;
    $[40] = t6;
    $[41] = t7;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
};
function _temp(tag) {
  return tag.factiiiId;
}
function _temp2(previousData) {
  return previousData;
}
function _temp3(t) {
  return t.factiiiId;
}
function _temp4(tag_1) {
  return convertToFactiiiTag(tag_1);
}